import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useCampaignMapStore } from '../stores/campaignMapStore';
import { useMapStore } from '../stores/mapStore';
import { Icon, type IconName } from './Icon';

/**
 * MapControlsToolbar
 *
 * Single bottom-right control cluster shared by MapCanvas + IsoMapCanvas.
 * Two stacked rows inside one pill:
 *   Row 1 — zoom (+/%/fit/-) plus any view-specific `extras` (iso torch, help)
 *   Row 2 — view mode (Campaign / Top-Down / Iso)
 *
 * Styling mirrors the left-side VerticalToolbar (44×44 buttons, surface bg,
 * gold-accent when active) so both clusters feel like one system.
 */

interface ModeDef {
  id: 'campaign' | 'battle' | 'iso';
  label: string;
  icon: IconName;
}

// Three explicit views. Each button = one specific mode. No "smart"
// last-used routing — the top-down slot and iso slot are independent and
// the GM picks explicitly. Prevents the dual-highlight confusion and the
// "clicked Battle Map, landed somewhere unexpected" surprise.
const MODES: ModeDef[] = [
  { id: 'campaign', label: 'Campaign Map', icon: 'map' },
  { id: 'battle', label: 'Top-Down Battle Map', icon: 'sword' },
  { id: 'iso', label: 'Isometric Battle Map', icon: 'diamond' },
];

const MIN_ZOOM = 0.25;
const MAX_ZOOM = 4;
const ZOOM_STEP = 0.1;

function btnStyle(isActive: boolean): React.CSSProperties {
  return {
    width: 44,
    height: 44,
    borderRadius: 'var(--radius-md)',
    backgroundColor: isActive ? 'var(--color-primary)' : 'var(--color-background-elevated)',
    color: isActive ? 'var(--color-text)' : 'var(--color-text-secondary)',
    border: `var(--border-width-thin) solid ${isActive ? 'var(--color-accent)' : 'var(--color-border)'}`,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all var(--transition-fast)',
    fontWeight: 600,
    fontSize: 11,
  };
}

// No hover handlers — mutating inline styles via e.currentTarget.style can
// leave stale backgrounds when the button's isActive flips mid-hover (was
// causing two buttons to both appear active on mode switch). Rely purely
// on the style prop from btnStyle(isActive).
function hoverHandlers(_isActive: boolean) {
  return {};
}

interface MapControlsToolbarProps {
  bottom: string;
  /** Fit-to-window handler — omit on views that don't support it (iso). */
  onFit?: () => void;
  /** Optional extra buttons rendered at the start of row 1 (e.g. iso torch/help). */
  extras?: ReactNode;
}

export function MapControlsToolbar({ bottom, onFit, extras }: MapControlsToolbarProps) {
  const mapMode = useCampaignMapStore((s) => s.mapMode);
  const setMapMode = useCampaignMapStore((s) => s.setMapMode);
  const zoom = useMapStore((s) => s.zoom);
  const setZoom = useMapStore((s) => s.setZoom);

  // Draggable cluster — same interaction as the VerticalToolbar: grab
  // anywhere that isn't a control, with a grip as the visual affordance.
  // Position is stored relative to the parent so it survives canvas resizes
  // the same way the default bottom/right anchoring does.
  const [position, setPosition] = useState<{ x: number; y: number } | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef<HTMLDivElement>(null);
  const dragStartRef = useRef<{ mouseX: number; mouseY: number; elemX: number; elemY: number } | null>(null);

  const handleDragStart = useCallback((e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest('button') || target.closest('input') || target.closest('select')) return;

    e.preventDefault();
    setIsDragging(true);

    const rect = dragRef.current?.getBoundingClientRect();
    const parentRect = dragRef.current?.parentElement?.getBoundingClientRect();
    if (rect && parentRect) {
      dragStartRef.current = {
        mouseX: e.clientX,
        mouseY: e.clientY,
        elemX: rect.left - parentRect.left,
        elemY: rect.top - parentRect.top,
      };
    }
  }, []);

  const handleDrag = useCallback((e: MouseEvent) => {
    if (!isDragging || !dragStartRef.current) return;
    setPosition({
      x: dragStartRef.current.elemX + (e.clientX - dragStartRef.current.mouseX),
      y: dragStartRef.current.elemY + (e.clientY - dragStartRef.current.mouseY),
    });
  }, [isDragging]);

  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
    dragStartRef.current = null;
  }, []);

  useEffect(() => {
    if (!isDragging) return;
    window.addEventListener('mousemove', handleDrag);
    window.addEventListener('mouseup', handleDragEnd);
    return () => {
      window.removeEventListener('mousemove', handleDrag);
      window.removeEventListener('mouseup', handleDragEnd);
    };
  }, [isDragging, handleDrag, handleDragEnd]);

  const zoomIn = () => setZoom(Math.min(MAX_ZOOM, zoom + ZOOM_STEP));
  const zoomOut = () => setZoom(Math.max(MIN_ZOOM, zoom - ZOOM_STEP));
  const zoomReset = () => setZoom(1);
  const isCustomZoom = Math.abs(zoom - 1) > 0.01;

  return (
    <div
      ref={dragRef}
      onMouseDown={handleDragStart}
      style={{
        position: 'absolute',
        // Once dragged, left/top replaces the bottom/right anchor outright —
        // keeping both would fight over the same axis.
        ...(position
          ? { left: position.x, top: position.y }
          : { bottom, right: 'var(--spacing-md)' }),
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 'var(--spacing-xs)',
        backgroundColor: 'var(--color-surface)',
        padding: 'var(--spacing-sm)',
        borderRadius: 'var(--radius-lg)',
        border: 'var(--border-width-normal) solid var(--color-border)',
        boxShadow: 'var(--shadow-lg)',
        zIndex: 100,
        pointerEvents: 'auto',
        cursor: isDragging ? 'grabbing' : 'default',
        userSelect: 'none',
      }}
    >
      {/* Drag handle — the VerticalToolbar's grip, rotated. That toolbar is a
          tall column so its grip lies flat across the top; this cluster is a
          wide two-row block, so the grip stands vertically down the left edge,
          which is the side with no buttons on it. */}
      <div
        style={{
          width: '6px',
          alignSelf: 'stretch',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          cursor: 'grab',
        }}
        title="Drag to move"
      >
        <div
          style={{
            width: '3px',
            height: '24px',
            backgroundColor: 'var(--color-border)',
            borderRadius: 'var(--radius-full)',
          }}
        />
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-xs)' }}>
      {/* Row 1 — zoom + extras */}
      <div style={{ display: 'flex', flexDirection: 'row', gap: 'var(--spacing-xs)' }}>
        {extras}
        <button onClick={zoomIn} title="Zoom In" style={btnStyle(false)} {...hoverHandlers(false)}>
          <Icon name="plus" size={18} />
        </button>
        <button
          onClick={zoomReset}
          title={isCustomZoom ? 'Reset to 100%' : 'Zoom 100%'}
          style={{
            // Distinct amber style when zoom is off 100% — avoids looking
            // like an active MODE button (which uses primary pink).
            width: 44,
            height: 44,
            borderRadius: 'var(--radius-md)',
            backgroundColor: isCustomZoom ? 'rgba(212, 175, 55, 0.15)' : 'var(--color-background-elevated)',
            color: isCustomZoom ? '#d4af37' : 'var(--color-text-secondary)',
            border: `var(--border-width-thin) solid ${isCustomZoom ? 'rgba(212, 175, 55, 0.5)' : 'var(--color-border)'}`,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all var(--transition-fast)',
            fontWeight: 600,
            fontSize: 11,
          }}
        >
          {Math.round(zoom * 100)}%
        </button>
        {onFit && (
          <button onClick={onFit} title="Fit map to window" style={btnStyle(false)} {...hoverHandlers(false)}>
            <span style={{ fontSize: 16, lineHeight: 1 }}>⊡</span>
          </button>
        )}
        <button onClick={zoomOut} title="Zoom Out" style={btnStyle(false)} {...hoverHandlers(false)}>
          <Icon name="minus" size={18} />
        </button>
      </div>

      {/* Row 2 — mode toggle (explicit, one button per view) */}
      <div style={{ display: 'flex', flexDirection: 'row', gap: 'var(--spacing-xs)' }}>
        {MODES.map((mode) => {
          const isActive = mapMode === mode.id;
          return (
            <button
              key={mode.id}
              onClick={() => setMapMode(mode.id)}
              title={mode.label}
              style={btnStyle(isActive)}
              {...hoverHandlers(isActive)}
            >
              <Icon name={mode.icon} size={20} />
            </button>
          );
        })}
      </div>
      </div>
    </div>
  );
}

// Legacy export preserved for any older imports. Prefer MapControlsToolbar.
export { MapControlsToolbar as MapModeToolbar };
